import React, {useEffect, useRef, useState} from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {DatePicker} from "../../common/DatePicker";
import Editor from "../../common/Editor";
import {MODE_CREATE, MODE_EDIT} from "../../codes";
import moment from "moment";
import useToast from "../../util/useToast";
import {useMutation} from "@tanstack/react-query";
import {apiCreateEvent, apiDeleteEvent, apiUpdateEvent} from "../../api/ApiEvents";
import {DeleteModal} from "../../common/AppModal";
import {EventErrors} from "../../errors/ErrorsMapping";
import { TextEditor } from "../../lexical/TextEditor";

const SiteEventEditor = ({show, mode, hide, site, event, onSubmit = null, onDelete}) => {
  const {showToastSuccess} = useToast();

  const [deleteModal, setDeleteModal] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    date: moment().format("YYYY-MM-DD")
  });

  const nameRef = useRef(null);
  const editorRef = useRef(null);

  const createMutation = useMutation(apiCreateEvent());
  const editMutation = useMutation(apiUpdateEvent(event.id));
  const deleteMutation = useMutation(apiDeleteEvent(event.id));

  useEffect(() => {
    if (show) {
      setFormErrors({});

      setFormData({
        siteId: site.id,
        name: event.name || "",
        description: event.description || "",
        date: event.date || moment().format("YYYY-MM-DD"),
      });

      nameRef.current.focus();
    }
  }, [show, event, site.id]);

  const submit = () => {
    let promise;

    const submitData = {
      ...formData,
      description: editorRef.current?.getHtml() || ""
    };

    if (mode === MODE_EDIT) {
      promise = editMutation.mutateAsync(submitData);
    } else {
      promise = createMutation.mutateAsync(submitData);
    }

    return promise
      .then(() => {
        return (onSubmit ? onSubmit() : Promise.resolve()).then(() => {
          if (mode === MODE_EDIT) {
            showToastSuccess("Événement modifié");
          } else {
            showToastSuccess("Evénement ajouté");
          }
        });
      })
      .then(hide)
      .catch((err) => {
        if (err.response?.status === 400) {
          setFormErrors(err.response.data.errors);
        }
      });
  };

  const updateFormData = field => e => {
    const values = {};

    switch (field) {
      case "date":
        values[field] = e;
        break;
      default:
        values[field] = e.target.value;
        break;
    }

    setFormData(prev => ({...prev, ...values}));
  };

  return <Editor
    show={show}
    title={mode === MODE_CREATE ? "Ajouter un événement" : "Modifier un événement"}
    onSubmit={submit}
    onCancel={hide}
    onDelete={() => setDeleteModal(true)}
    submitLabel="Sauvegarder"
    cancelLabel="Annuler"
  >
    <Form.Group className="mb-2" controlId="site-event-editor-name">
      <Row>
        <Form.Label column sm={3}>Nom</Form.Label>
        <Col sm={9}>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={updateFormData("name")}
            maxLength="255"
            ref={nameRef}
            isInvalid={!!formErrors.name}
          />
          <Form.Control.Feedback type="invalid">{EventErrors.name[formErrors.name]}</Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-event-editor-date">
      <Row>
        <Form.Label column sm={3}>Date</Form.Label>
        <Col sm={9}>
          <DatePicker
            value={formData.date}
            onChange={updateFormData("date")}
            isInvalid={!!formErrors.date}
            error={EventErrors.date[formErrors.date]}
          />
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-event-editor-description">
      <Row>
        <Form.Label column sm={3}>Description</Form.Label>
        <Col sm={9}>
          <TextEditor ref={editorRef} value={formData.description} />
        </Col>
      </Row>
    </Form.Group>

    <DeleteModal
      show={deleteModal}
      title="Supprimer l'événement"
      onCancel={() => setDeleteModal(false)}
      onSubmit={() => {
        return deleteMutation.mutateAsync(null)
          .then(onDelete)
          .then(() => {
            showToastSuccess("Événement supprimé");

            hide();
            setDeleteModal(false);
          });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer cet événement ?
    </DeleteModal>
  </Editor>;
};

export default SiteEventEditor;